import { Amplify } from "aws-amplify";
import { client, graphqlOperation } from "../libs/amplifyAPI";
import { AWS_CONFIGURATION, PUBLIC_HOLIDAYS } from './../config';
import listDoctorsNames from './../queries/listDoctorsNames';
import listTodos from './../queries/listTodos';
import { nullifyPatient, formatRequest, buildSearchableText } from './../libs/general';
import access from 'safe-access';
import moment from 'moment';

import listAppointmentsQuery from './../queries/listAppointments';
import listPendingQuery from './../queries/listPending';
import searchAppointmentsQuery from './../queries/searchAppointments';
import getAppointmentQuery from './../queries/getAppointment';

import createAppointmentMutation from './../mutations/createAppointment';
import deleteAppointmentMutation from './../mutations/deleteAppointment';
import splitAppointmentMutation from './../mutations/splitAppointment';
import updateAppointmentMutation from './../mutations/updateAppointment';

import getDoctorQuery from './../queries/getDoctor';
import { getCurrentUserId, getCurrentUserRights } from './../libs/auth';

Amplify.configure(AWS_CONFIGURATION);

export const getInitialInfo = async () => {
	try {
		const rights = await getCurrentUserRights();
		const userId = await getCurrentUserId(); // Renamed to clarify this is the user's ID, not a doctor ID

		console.log('Current user rights and userId:', { rights, userId }); // Updated log message for clarity

		if (!rights || !userId) {
			console.warn('Missing user rights or ID in getInitialInfo', { rights, userId });
			return {
				alphabeticalList: [],
				favouritesList: [],
				todosList: []
			};
		}

		// Verify we have valid auth
		try {
			const { fetchAuthSession } = await import('aws-amplify/auth');
			const authSession = await fetchAuthSession();
			if (!authSession?.tokens?.idToken) {
				console.error('No valid auth token available for fetching initial data');
				throw new Error('Authentication problem: Please sign out and sign back in');
			}
			
			console.log('Auth session valid, token exists');
		} catch (authError) {
			console.error('Error checking auth session:', authError);
			throw new Error('Authentication problem: Please sign out and sign back in');
		}

		if (rights === "doctor") {
			try {
				// For doctor users, their userId is also their doctorId
				console.log('Fetching doctor data with doctorId:', userId);
				const tempDoc = await client.graphql({
					query: getDoctorQuery,
					variables: { docId: userId }
				});
				
				console.log('Doctor data response:', tempDoc);
				
				if (!tempDoc?.data?.getDoctor?.doctor) {
					console.error('Invalid doctor data response:', tempDoc);
					return {
						alphabeticalList: [],
						favouritesList: [],
						todosList: []
					};
				}
				
				const doc = tempDoc.data.getDoctor.doctor;
				return {
					alphabeticalList: [doc],
					favouritesList: [doc],
					todosList: []
				};
			} catch (doctorError) {
				console.error('Error fetching doctor data:', doctorError);
				console.error('Doctor error details:', JSON.stringify(doctorError, null, 2));
				return {
					alphabeticalList: [],
					favouritesList: [],
					todosList: []
				};
			}
		} else if (rights === "admin" || rights === "limitedadmin") {
			try {
				console.log('Fetching admin data for rights:', rights);
				
				// First try to get alphabetical list
				let alphabeticalList = [];
				try {
					const alphabeticalListResponse = await client.graphql({
						query: listDoctorsNames,
						variables: { isFavouriteOnly: false }
						});
					
					if (alphabeticalListResponse.errors) {
						console.error('GraphQL errors in alphabetical list response:', alphabeticalListResponse.errors);
						// Log the full details of each error
						alphabeticalListResponse.errors.forEach((err, index) => {
							console.error(`Error ${index + 1}:`, err);
							if (err.message) console.error('Message:', err.message);
							if (err.path) console.error('Path:', err.path);
							if (err.locations) console.error('Locations:', err.locations);
							if (err.extensions) console.error('Extensions:', err.extensions);
						});
					} else {
						console.log('Alphabetical list response:', alphabeticalListResponse);
						alphabeticalList = alphabeticalListResponse?.data?.listDoctors?.items || [];
					}
				} catch (alphaError) {
					console.error('Error fetching alphabetical list:', alphaError);
					// Print full error details
					if (alphaError.networkError) {
						console.error('Network error details:', alphaError.networkError);
						console.error('Status code:', alphaError.networkError.statusCode);
					}
					if (alphaError.graphQLErrors) {
						console.error('GraphQL errors:', alphaError.graphQLErrors);
					}
				}
				
				// Then try to get favorites list
				let favouritesList = [];
				try {
					const favouritesListResponse = await client.graphql({
						query: listDoctorsNames,
						variables: { isFavouriteOnly: true }
						});
					
					if (favouritesListResponse.errors) {
						console.error('GraphQL errors in favourites list response:', favouritesListResponse.errors);
						// Log the full details of each error
						favouritesListResponse.errors.forEach((err, index) => {
							console.error(`Error ${index + 1}:`, err);
							if (err.message) console.error('Message:', err.message);
							if (err.path) console.error('Path:', err.path);
							if (err.locations) console.error('Locations:', err.locations);
							if (err.extensions) console.error('Extensions:', err.extensions);
						});
					} else {
						console.log('Favourites list response:', favouritesListResponse);
						favouritesList = favouritesListResponse?.data?.listDoctors?.items || [];
					}
				} catch (favError) {
					console.error('Error fetching favourites list:', favError);
					// Print full error details
					if (favError.networkError) {
						console.error('Network error details:', favError.networkError);
						console.error('Status code:', favError.networkError.statusCode);
					}
					if (favError.graphQLErrors) {
						console.error('GraphQL errors:', favError.graphQLErrors);
					}
				}
				
				// Then try to get todos list
				let todosList = [];
				try {
					const todosListResponse = await client.graphql({
						query: listTodos
						});
					
					if (todosListResponse.errors) {
						console.error('GraphQL errors in todos list response:', todosListResponse.errors);
						// Log the full details of each error
						todosListResponse.errors.forEach((err, index) => {
							console.error(`Error ${index + 1}:`, err);
							if (err.message) console.error('Message:', err.message);
							if (err.path) console.error('Path:', err.path);
							if (err.locations) console.error('Locations:', err.locations);
							if (err.extensions) console.error('Extensions:', err.extensions);
						});
					} else {
						console.log('Todos list response:', todosListResponse);
						todosList = todosListResponse?.data?.listTodos?.items || [];
					}
				} catch (todosError) {
					console.error('Error fetching todos list:', todosError);
					// Print full error details
					if (todosError.networkError) {
						console.error('Network error details:', todosError.networkError);
						console.error('Status code:', todosError.networkError.statusCode);
					}
					if (todosError.graphQLErrors) {
						console.error('GraphQL errors:', todosError.graphQLErrors);
					}
				}
				
				return {
					alphabeticalList,
					favouritesList,
					todosList
				};
			} catch (adminError) {
				console.error('Error fetching admin data:', adminError);
				console.error('Admin error details:', JSON.stringify(adminError, null, 2));
				return {
					alphabeticalList: [],
					favouritesList: [],
					todosList: []
				};
			}
		} else {
			console.warn(`Unhandled user rights: ${rights}`);
			return {
				alphabeticalList: [],
				favouritesList: [],
				todosList: []
			};
		}
	} catch (e) {
		console.error('Error in getInitialInfo:', e);
		return {
			alphabeticalList: [],
			favouritesList: [], 
			todosList: []
		};
	}
}

/**
 * Fetches appointments for a doctor within a date range.
 * @param {string} docId - Doctor ID
 * @param {string} startDate - Start date (ISO string)
 * @param {string} endDate - End date (ISO string)
 * @param {boolean} forceGeneration - (optional) Force regeneration
 */
export const getAppointments = async (docId, startDate, endDate, forceGeneration = false) => {
  try {
    if (!docId) {
      return new Error('Doctor ID is required');
    }
    
    const dateKey = `${startDate || 'all'}_${endDate || 'all'}`;
    
    // Verify we have valid auth
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const authSession = await fetchAuthSession();
      if (!authSession?.tokens?.idToken) {
        console.error('No valid auth token available for appointment fetch');
        return new Error('Authentication problem: Please sign out and sign back in');
      }
    } catch (authError) {
      console.error('Error checking auth session:', authError);
      return new Error('Authentication problem: Please sign out and sign back in');
    }

    
    // Build the query parameters
    let variables = { docId };
    
    // Always require both dates for better query precision
    if (!startDate || !moment(startDate).isValid()) {
      startDate = moment().subtract(1, 'month').startOf('month').toISOString();
    }
    
    if (!endDate || !moment(endDate).isValid()) {
      endDate = moment().add(2, 'month').endOf('month').toISOString();
    }
    
    // Always ensure start is before end (or equal)
    const start = moment.min(moment(startDate), moment(endDate));
    const end = moment.max(moment(startDate), moment(endDate));
    
    variables.startDate = start.toISOString();
    variables.endDate = end.toISOString();
    
    
    // Execute the GraphQL query
    const result = await client.graphql(graphqlOperation(
      listAppointmentsQuery,
      variables
    ));
    
    // Error handling for GraphQL errors
    if (result.errors) {
      console.error('GraphQL errors in appointment fetch:', result.errors);
      return new Error('Error fetching appointments');
    }
    
    // Extract appointments from the response
    const dbAppointments = result.data.listScheduleItems.items;
    
    // Apply filtering and generate auto appointments
    const appointments = filterAppointments(dbAppointments, startDate, endDate, forceGeneration);
    
    return appointments;
  } catch (error) {
    console.error('Error fetching appointments:', error);
    
    // Error handling for different kinds of errors
    if (error.name === 'UserNotAuthorizedException') {
      return new Error('Not authorized: Please sign out and sign back in');
    }
    
    if (error instanceof Error) {
      return error;
    }
    
    return new Error('Failed to get appointments');
  }
};

const filterAppointments = (appointments, startDate, endDate, forceGeneration = false) => {
  
  // Create a map of timestamp to filled appointments for quick lookups
  const filledAppointmentTimestamps = new Map();
  
  // First pass: record timestamps of all filled appointments
  appointments.forEach(app => {
    if (app.type === 'filled') {
      const timestamp = moment(app.fromTimestamp).format(); // Get standardized timestamp format
      filledAppointmentTimestamps.set(timestamp, true);
    }
  });
  
  // Second pass: filter out non-filled appointments that have the same timestamp as a filled one
  const filteredAppointments = appointments.filter(app => {
    // Keep all filled appointments
    if (app.type === 'filled') return true;
    
    // For non-filled appointments, check if there's a filled one at the same time
    const timestamp = moment(app.fromTimestamp).format();
    return !filledAppointmentTimestamps.has(timestamp);
  });

  
  // Generate auto appointments with the specified date range
  const autoAppointments = getAutoAppointments(filteredAppointments, startDate, endDate, forceGeneration);
  
  // Process each appointment to ensure consistent handling
  const processedAppointments = [...autoAppointments, ...filteredAppointments].map(normalizeAppointmentType);
  
  return processedAppointments;
};

// This helper function ensures auto_available appointments are treated the same as regular available appointments
export const normalizeAppointmentType = (app) => {
  // Create a copy of the appointment to avoid modifying the original
  const normalizedApp = { ...app };
  
  // If it's an auto_available appointment, add a flag that the UI components can use
  if (app.type === 'auto_available') {
    normalizedApp.isAutoAvailable = true;
    normalizedApp.autoGenerated = true; // More explicit flag for debugging
  }
  
  return normalizedApp;
};

const getAutoAppointments = (dbApps, startDate, endDate, forceGeneration = false) => {
  let autoSchedule = [];
  let startDateObj = startDate ? moment(startDate) : moment().startOf('month');
  let endDateObj = endDate ? moment(endDate) : moment().add(1, 'month').endOf('month');
  
  
  try {
    // This is where we get the doctor's schedule configuration from localStorage
    autoSchedule = JSON.parse(window.localStorage.getItem('currentDoctorSchedule'));
    
    
  } catch (e) {
    return [];
  }
  
  // If no schedule or empty schedule, return empty array
  if (!autoSchedule || !Array.isArray(autoSchedule) || autoSchedule.length === 0) {
    return [];
  }

  const toReturn = [];

  // Sort dbApps by fromTimestamp for efficient processing
  if (dbApps && Array.isArray(dbApps)) {
    dbApps.sort((a, b) => {
      const timeA = moment(a.fromTimestamp);
      const timeB = moment(b.fromTimestamp);
      return timeA.valueOf() - timeB.valueOf();
    });
  }

  for (const s of autoSchedule) {
    // Skip disabled schedules
    if (s.type === 'disabled') {
      continue;
    }
    
    
    // Initialize schedule-specific variables
    let lastDay = endDateObj.clone();
    
    // Initialization of the schedule start date
    const scheduleStartDate = moment(s.startDate);
    
    
    // Limit how far ahead we generate based on schedule type
    switch (s.type) {
      case 'daily':
        if (lastDay.isAfter(moment().add(2, 'week'))) {
          lastDay = moment().add(2, 'week');
        }
        break;
      case 'weekly':
      case 'every1week':
        if (lastDay.isAfter(moment().add(6, 'month'))) {
          lastDay = moment().add(6, 'month');
        }
        break;
      case 'every2weeks':
      case 'every3weeks':
        if (lastDay.isAfter(moment().add(1, 'year'))) {
          lastDay = moment().add(1, 'year');
        }
        break;
      case 'monthly':
        if (lastDay.isAfter(moment().add(2, 'year'))) {
          lastDay = moment().add(2, 'year');
        }
        break;
      case 'every2months':
        if (lastDay.isAfter(moment().add(4, 'year'))) {
          lastDay = moment().add(4, 'year');
        }
        break;
      case 'every6months':
        if (lastDay.isAfter(moment().add(10, 'year'))) {
          lastDay = moment().add(10, 'year');
        }
        break;
      case 'yearly':
        if (lastDay.isAfter(moment().add(20, 'year'))) {
          lastDay = moment().add(20, 'year');
        }
        break;
      default:
        if (lastDay.isAfter(moment().add(1, 'year'))) {
          lastDay = moment().add(1, 'year');
        }
        break;
    }

    // Start from the beginning of the date range or the start date, whichever is later
    const currentDay = startDateObj.isAfter(scheduleStartDate) ? 
      startDateObj.clone() : scheduleStartDate.clone();

    let isPublicHoliday = false;
    
    // Improved pattern-specific variables
    // We'll calculate which iterations of the pattern should generate appointments
    let shouldGenerateAppointment = false;
    
    // For reference day calculations (when the pattern repeats)
    const startWeekday = scheduleStartDate.weekday();
    const startMonthDay = scheduleStartDate.date();
    
    // For more complex patterns
    let sixMonthsFlag = moment(s.startDate);

    let appPointer = 0;
    let generatedCount = 0;

    // Process each day in the date range
    while (currentDay.isSameOrBefore(lastDay, 'day')) {
      // Skip if outside the requested date range
      if (currentDay.isBefore(startDateObj) || currentDay.isAfter(endDateObj)) {
        currentDay.add(1, 'day');
        continue;
      }

      // Reset shouldGenerateAppointment flag for this day
      shouldGenerateAppointment = false;

      // Check if day is a public holiday
      isPublicHoliday = false;
      for (const ph of PUBLIC_HOLIDAYS) {
        let holiday;
        
        if (ph.isDynamic && ph.isDynamic === true) 
          holiday = moment(ph.date[currentDay.year().toString()]).year(currentDay.year());
        else holiday = moment(ph.date).year(currentDay.year());
        
        if (holiday && currentDay.isSame(holiday, 'day')) {
          isPublicHoliday = true;
          break;
        }
      }

      // Determine if we should generate appointments for this day based on the pattern type
      if (currentDay.isSameOrAfter(moment(), 'day')) {
        switch (s.type) {
          case 'daily':
            // Generate appointments every day except Saturdays (weekday 6)
            shouldGenerateAppointment = (currentDay.weekday() !== 6);
            break;
            
          case 'weekly':
          case 'every1week':
            // Generate appointments on the same weekday as the start date
            shouldGenerateAppointment = (currentDay.weekday() === startWeekday);
            break;
            
          case 'every2weeks': {
            // Only generate appointments if on the same weekday and the right week
            if (currentDay.weekday() === startWeekday) {
              // Calculate weeks since reference date
              const referenceStartDay = scheduleStartDate.clone().startOf('week');
              const currentStartOfWeek = currentDay.clone().startOf('week');
              
              // Calculate the week number difference (integer division by 7)
              const weeksDiff = currentStartOfWeek.diff(referenceStartDay, 'days') / 7;
              
              
              // Generate appointments every other week (when weeks difference is even)
              shouldGenerateAppointment = (Math.floor(weeksDiff) % 2 === 0);
            }
            break;
          }
          
          case 'every3weeks': {
            // Similar to every2weeks but with a 3-week cycle
            if (currentDay.weekday() === startWeekday) {
              const referenceStartDay = scheduleStartDate.clone().startOf('week');
              const currentStartOfWeek = currentDay.clone().startOf('week');
              const weeksDiff = currentStartOfWeek.diff(referenceStartDay, 'days') / 7;
              
              // Generate appointments every third week (when weeks difference mod 3 equals 0)
              shouldGenerateAppointment = (Math.floor(weeksDiff) % 3 === 0);
            }
            break;
          }
          
          case 'monthly':
            // Generate appointments on the same day of each month
            shouldGenerateAppointment = (currentDay.date() === startMonthDay);
            break;
            
          case 'every2months': {
            // Only generate appointments on the same day of alternating months
            if (currentDay.date() === startMonthDay) {
              const monthsDiff = (currentDay.year() - scheduleStartDate.year()) * 12 + 
                                 (currentDay.month() - scheduleStartDate.month());
              shouldGenerateAppointment = (monthsDiff % 2 === 0);
            }
            break;
          }
          
          case 'every6months': {
            // Only generate appointments on the same day every 6 months
            if (currentDay.date() === startMonthDay) {
              const monthsDiff = (currentDay.year() - scheduleStartDate.year()) * 12 + 
                                 (currentDay.month() - scheduleStartDate.month());
              shouldGenerateAppointment = (monthsDiff % 6 === 0);
            }
            break;
          }
          
          case 'yearly':
            // Generate appointments on the same day of the same month each year
            shouldGenerateAppointment = (currentDay.date() === startMonthDay && 
                                         currentDay.month() === scheduleStartDate.month());
            break;
            
          default:
            // For any unrecognized pattern, default to weekly
            shouldGenerateAppointment = (currentDay.weekday() === startWeekday);
            break;
        }
      }

      // If this day should have appointments, generate them
      if (shouldGenerateAppointment) {
        

        const startTime = moment(currentDay).set({
          hours: s.startHour,
          minutes: s.startMinute,
          seconds: 0,
          milliseconds: 0
        });
        const startTime2 = moment(currentDay).set({ //to avoid using startTime reference
          hours: s.startHour,
          minutes: s.startMinute,
          seconds: 0,
          milliseconds: 0
        });
        const endTime = moment(currentDay).set({
          hours: s.endHour,
          minutes: s.endMinute,
          seconds: 0,
          milliseconds: 0
        });
        let currentTimeStart = startTime;
        let currentTimeEnd = startTime2;
        currentTimeEnd.add(s.minsPerAppointment, 'minute');

        // eslint-disable-next-line 
        Number.prototype.zeroPad = function () {
          return ('0' + this).slice(-2);
        };

        const slotsForThisDay = [];
        
        while (currentTimeStart.isBefore(endTime, 'minute')) {
          // check if app from db already exist for that fromTimestamp
          const newFromString = moment(`${currentDay.year()}-${(currentDay.month() + 1).zeroPad()}-${currentDay.date().zeroPad()}T${currentTimeStart.hour().zeroPad()}:${currentTimeStart.minute().zeroPad()}`).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
          const newFrom = moment(newFromString); 
          const newToString = moment(`${currentDay.year()}-${(currentDay.month() + 1).zeroPad()}-${currentDay.date().zeroPad()}T${currentTimeEnd.hour().zeroPad()}:${currentTimeEnd.minute().zeroPad()}`).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
          let noReturn = false;

          // Skip auto-generation if a DB appointment exists at the same time
          // Use a more robust approach to check for overlaps
          if (dbApps && Array.isArray(dbApps)) {
            for (let i = appPointer; i < dbApps.length; i++) {
              const dbApp = dbApps[i];
              const dbFrom = moment(dbApp.fromTimestamp);
              
              // If we're already past the current time slot, stop checking
              if (dbFrom.isAfter(newFrom, 'minute')) {
                break;
              }
              
              // If there's an exact match, skip this auto slot
              if (dbFrom.isSame(newFrom, 'minute')) {
                noReturn = true;
                break;
              }
              
              // Move pointer forward when we've checked this appointment
              if (i === appPointer && dbFrom.isBefore(newFrom, 'minute')) {
                appPointer++;
              }
            }
          }
          
          if (!noReturn) {
            const autoAppointment = {
              docId: window.localStorage.getItem('currentDoctorId'),
              creationTimestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
              fromTimestamp: newFromString,
              toTimestamp: newToString,
              type: isPublicHoliday ? 'holiday_blocked' : 'auto_available',
              patient: null,
              clinic: null,
              pharmacyRemarks: null,
              doctorRemarks: null,
              billing: null
            };
            
            toReturn.push(autoAppointment);
            slotsForThisDay.push(moment(newFromString).format('HH:mm'));
            generatedCount++;
          }

          currentTimeStart.add(s.minsPerAppointment, 'minute');
          currentTimeEnd.add(s.minsPerAppointment, 'minute');
        }
        
      }

      currentDay.add(1, 'day');
    }
    
    // Log how many auto appointments were generated for this schedule
  }
  
  return toReturn;
}

// Helper function to convert weekday number to name for better logging
function weekdayName(weekday) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[weekday] || 'Unknown';
}

export const listPending = async (docId) => {
	try {
		const pendingList = await client.graphql(graphqlOperation(listPendingQuery, { docId }));
		return pendingList.data.getPendingList.items;
	} catch (e) {
		if (e instanceof Error) return e;
		else return new Error('Failed to get pending list');
	}
}

export const getAppointment = async (docId, creationTimestamp) => {
	try {
		const appointment = await client.graphql(graphqlOperation(getAppointmentQuery, { docId, creationTimestamp }));
		return appointment.data.getScheduleItem;
	} catch (e) {
		if (e instanceof Error) return e;
		else return new Error('Failed to get appointment');
	}
}

export const createAppointment = async (docId, params) => {
	try {
		const tempParams = Object.assign({}, nullifyPatient(params));
		if (access(tempParams, 'patient.mobile') && !access(tempParams,'patient.mobile.countryPrefixCode')) tempParams.patient.mobile.countryPrefixCode = 356;
		if (access(tempParams, 'patient.landline')) tempParams.patient.landline.countryPrefixCode = 356;
		if (tempParams.smsReminder) delete tempParams.smsReminder;
		if (tempParams.emailReminder) delete tempParams.emailReminder;
		if (tempParams.docId) delete tempParams.docId;
		if (tempParams.creationTimestamp) delete tempParams.creationTimestamp;
		if (tempParams.reminders || tempParams.reminders === null) delete tempParams.reminders;
		if (!tempParams.pharmacyRemarks) tempParams.pharmacyRemarks = null;
		if (!tempParams.doctorRemarks) tempParams.doctorRemarks = null;
		
		// Ensure both timestamps are properly formatted and not null
		if (!tempParams.fromTimestamp) {
			console.error('fromTimestamp is required but is missing');
			return new Error('fromTimestamp is required but is missing');
		}
		
		if (!tempParams.toTimestamp) {
			console.error('toTimestamp is required but is missing');
			return new Error('toTimestamp is required but is missing');
		}
		
		// Use UTC format to ensure consistent datetime strings for AWS
		tempParams.fromTimestamp = moment.utc(tempParams.fromTimestamp).format();
		tempParams.toTimestamp = moment.utc(tempParams.toTimestamp).format();
		
		// Additional validation check
		if (tempParams.fromTimestamp === 'Invalid date' || tempParams.toTimestamp === 'Invalid date') {
			console.error('Invalid date format detected', { from: tempParams.fromTimestamp, to: tempParams.toTimestamp });
			return new Error('Invalid date format detected');
		}

		// Generate searchableText from patient data
		if (tempParams.patient) {
			tempParams.searchableText = buildSearchableText(tempParams.patient);
		}

		let createBoth = false;
		if (tempParams.type === 'both'){
			createBoth = true;
			tempParams.type = 'available';
		} 

		const item = await client.graphql(graphqlOperation(createAppointmentMutation, {
			docId: formatRequest(docId),
			item: formatRequest(tempParams)
		}));

		if (item && item.errors && item.errors instanceof Array && item.errors.length >= 1)
			return new Error(item.errors);
		else if (item && item.data && item.data.createScheduleItem){
			if (createBoth === true){
				tempParams.type = 'pending';
				await client.graphql(graphqlOperation(createAppointmentMutation, {
					docId: formatRequest(docId),
					item: formatRequest(tempParams)
				}));
			}
			
			return item.data.createScheduleItem;
		}			
		else return new Error('Failed to create appointment');
	} catch (e) {
		if (e instanceof Error) return e;
		else return new Error('Failed to create appointment');
	}
}

export const updateAppointment = async (docId, creationTimestamp, params) => {
	try {
		// Create a deep copy to avoid modifying the original data
		const tempParams = Object.assign({}, nullifyPatient(params));
		
		// Clean up unnecessary fields that shouldn't be sent to the backend
		if (params && params.docId) delete tempParams.docId;
		if (params && params.smsReminder) delete tempParams.smsReminder;
		if (params && params.emailReminder) delete tempParams.emailReminder;
		if (params && params.creationTimestamp) delete tempParams.creationTimestamp;
		if (params && (params.reminders || tempParams.reminders === null)) delete tempParams.reminders;
		
		// Ensure clinic field is preserved exactly as the user selected it
		// Don't fall back to empty string unless it's actually missing
		if (!tempParams.clinic && params.clinic) {
			tempParams.clinic = params.clinic;
		}
		
		// Handle patient fields
		if (tempParams.patient) {
			// Ensure remindManually is properly preserved and converted to boolean
			if (tempParams.patient.remindManually !== undefined) {
				// Convert string "true"/"false" to actual boolean
				const remindManuallyValue = String(tempParams.patient.remindManually).toLowerCase();
				tempParams.patient.remindManually = (remindManuallyValue === 'true');
			} else if (params.patient && params.patient.remindManually !== undefined) {
				// Use the original value if missing in tempParams
				const remindManuallyValue = String(params.patient.remindManually).toLowerCase();
				tempParams.patient.remindManually = (remindManuallyValue === 'true');
			} else {
				// Default to true if missing
				tempParams.patient.remindManually = true;
			}
			
			// Handle mobile number fields
			if (!access(tempParams, 'patient.mobile.countryPrefixCode')) {
				tempParams.patient.mobile.countryPrefixCode = 356;
			}
			
			// Handle landline number fields
			if (access(tempParams, 'patient.landline') && !access(tempParams, 'patient.landline.countryPrefixCode')) {
				tempParams.patient.landline.countryPrefixCode = 356;
			}
		}
		
		// Ensure other required fields have defaults
		if (!tempParams.pharmacyRemarks) tempParams.pharmacyRemarks = null;
		if (!tempParams.doctorRemarks) tempParams.doctorRemarks = null;
		
		// Format timestamps properly
		if (tempParams.fromTimestamp) tempParams.fromTimestamp = moment.utc(tempParams.fromTimestamp).format();
		if (tempParams.toTimestamp) tempParams.toTimestamp = moment.utc(tempParams.toTimestamp).format();

		// Generate searchableText from patient data
		if (tempParams.patient) {
			tempParams.searchableText = buildSearchableText(tempParams.patient);
		}

		// Log the final data being sent for debugging
		console.log('Updating appointment with data:', {
			docId,
			creationTimestamp,
			clinic: tempParams.clinic,
			remindManually: access(tempParams, 'patient.remindManually'),
			searchableText: tempParams.searchableText
		});

		// Execute the GraphQL mutation
		const item = await client.graphql(graphqlOperation(updateAppointmentMutation,
			{
				docId: formatRequest(docId),
				creationTimestamp: formatRequest(creationTimestamp),
				item: formatRequest(tempParams)
			}));

		// Handle potential errors or return successful result
		if (item && item.errors && item.errors instanceof Array && item.errors.length >= 1)
			return new Error(item.errors);
		else if (item && item.data && item.data.updateScheduleItem) {
			return item.data.updateScheduleItem;
		}
		else return new Error('Failed to update appointment');
	} catch (e) {
		console.error('Error updating appointment:', e);
		if (e instanceof Error) return e;
		else return new Error('Failed to update appointment');
	}
}

export const splitAppointment = async (docId, creationTimestamp) => {
  try {
    const result = await client.graphql(graphqlOperation(splitAppointmentMutation, { docId, creationTimestamp }));
    return result;
  } catch (error) {
    console.error('Error splitting appointment:', error);
    return error;
  }
};

export const deleteAppointment = async (docId, creationTimestamp) => {
  try {
    const result = await client.graphql(graphqlOperation(deleteAppointmentMutation, { docId, creationTimestamp }));
    return result;
  } catch (error) {
    console.error('Error deleting appointment:', error);
    return error;
  }
};

export const searchAppointments = async (docId, query) => {
	try {
		console.log('Searching appointments with:', { docId, query });
		
		const searchResult = await client.graphql(graphqlOperation(searchAppointmentsQuery, {
			docId: formatRequest(docId),
			query: formatRequest(query)
		}));
		
		console.log('Search result:', searchResult);
		
		if (searchResult && searchResult.data && searchResult.data.searchScheduleItems) {
			return searchResult.data.searchScheduleItems.items;
		} else {
			console.warn('Unexpected search result structure:', searchResult);
			return [];
		}
	} catch (e) {
		console.error('Error in searchAppointments:', e);
		if (e instanceof Error) return e;
		else return new Error('Failed to get search results');
	}
}

/**
 * Dedicated function to get the first available slots regardless of current calendar view
 * @param {string} docId - Doctor ID
 * @param {number} months - Number of months to look ahead
 * @param {number} limit - Maximum number of slots to return
 * @returns {Array} - Array of available appointment slots
 */
export const getFirstAvailableSlots = async (docId, months = 6, limit = 5) => {
  try {
    if (!docId) {
      console.error('No doctor ID provided to getFirstAvailableSlots');
      return new Error('Doctor ID is required');
    }
    
    // Calculate a wider date range for availability search
    const startDate = moment().startOf('day').toISOString();
    const endDate = moment().add(months, 'months').endOf('month').toISOString();
    
    console.log(`Fetching first available slots for doctor ${docId} from ${startDate} to ${endDate}`);
    
    // Fetch appointments for the extended range
    const appointments = await getAppointments(
      docId,
      startDate,
      endDate
    );
    
    if (appointments instanceof Error) {
      return appointments; // Return the error
    }
    
    if (!Array.isArray(appointments)) {
      return new Error('Invalid response when fetching available slots');
    }
    
    // Filter and process appointments to find available slots
    const availableSlots = appointments
      .filter(app => 
        (app.type === 'available' || app.type === 'auto_available') &&
        moment(app.fromTimestamp).isSameOrAfter(moment(), 'minute')
      )
      .sort((a, b) => moment(a.fromTimestamp).valueOf() - moment(b.fromTimestamp).valueOf())
      .slice(0, limit);
      
    return availableSlots;
  } catch (e) {
    console.error('Error fetching first available slots:', e);
    if (e instanceof Error) return e;
    return new Error('Failed to get first available slots');
  }
}